import { AdminDomain } from '../../../enums/AdminDomain';
import { ContentType } from '../../../enums/ContentType';
import { FieldType } from '../../../enums/FieldType';
import { ItemStatus } from '../../../enums/ItemStatus';
import { ItemType } from '../../../enums/ItemType';
import { JobRole } from '../../../enums/JobRole';
import { NodeType } from '../../../enums/NodeType';
import { Role } from '../../../enums/Role';
import { Metadata, Schema } from '../../../types';
import {
  getIntersection,
  getLockedNodeTypes,
  getInvalidItemDataReference,
  getItemContentTypeId,
  getItemStatusId,
  getItemType,
  isAuditType,
  isChildContextualization,
  isRemovedOrRetired
} from '../../../utils';
import {
  isWIP,
  isLightUpdate,
  isPendingRepublishing,
  isPublished,
  isPublishedOrCompletedInCPP
} from '../../../utils/status';
import { GetPermissionFn, GetPermissionPayload, Permission } from './types';

export const getSchemaFieldPermission: GetPermissionFn = (p): Permission => {
  const {
    workspace,
    isWorkspaceOwner,
    isItemTypePlanner,
    hasRole,
    hasJobRole,
    schemaField,
    data
  } = p;
  if (!data || !schemaField) return { canView: true, canEdit: false };
  if (hasRole(Role.planningReadonly)) {
    return { canView: true, canEdit: false };
  }

  const { key, isSystemField, dataSchema, fieldType, readOnly, parentField } =
    schemaField;
  const type = dataSchema?.props?.type;
  if (!type) return { canView: true, canEdit: false };

  if (type != NodeType.Item) return getNonItemFieldPermission(p);

  if (parentField == 'maintenanceCOE') return getMaintenceCOEFieldPermission(p);
  if (parentField == 'backtag') return getBacktagFieldPermission(p);
  const itemType = getItemType(data);
  const contentType = getItemContentTypeId(data);

  if (!!getInvalidItemDataReference(workspace, schemaField.dataSchema, data)) {
    // Disable item fields for CN-1261 and CN-1201
    return { canView: true, canEdit: false };
  }

  if (readOnly || fieldType == FieldType.computed) {
    return { canView: true, canEdit: false };
  }

  if (isSystemField && !dataSchema?.props?.allowAddDataWithBaseSchema) {
    return { canView: true, canEdit: false };
  }

  const statusId = getItemStatusId(data);
  const itemId = data.props?.itemId;
  let canEdit = true;
  if (
    (key != 'Item.configureWorkflowType' && isSystemField && !!itemId) ||
    statusId == ItemStatus.PendingCPPItemCreation
  ) {
    canEdit = false;
  }

  if (isRemovedOrRetired(statusId)) {
    canEdit = false;
  }

  switch (key) {
    case 'Item.relationship':
      canEdit = true; // Specially handled in relationship field
      break;
    case 'Item.status':
      if (
        !!statusId &&
        ![
          ItemStatus.Idea,
          ItemStatus.Concept_Review,
          ItemStatus.Not_Pursued,
          ItemStatus.PendingRetirement
        ].includes(statusId)
      ) {
        canEdit = false;
      }
      break;
    case 'Item.bigRocks':
      if ([ItemStatus.Not_Pursued, ItemStatus.Removed].includes(statusId)) {
        canEdit = false;
      }
      if (data?.props?.shouldAutocheckAndDisableBigRocks == true) {
        canEdit = false;
      }
      break;
    case 'Item.inThePlan':
      if (!isItemTypePlanner && !isWorkspaceOwner) {
        canEdit = false;
      } else if (isWIP(statusId)) {
        canEdit = true;
      }
      break;
    case 'Item.critical':
      if (
        (hasRole(Role.admin) || hasJobRole(JobRole.TeamManager)) &&
        isWIP(statusId)
      ) {
        canEdit = true;
      } else {
        canEdit = false;
      }
      break;
    case 'Item.contentType':
      if (
        isChildContextualization(p.data) ||
        [ItemStatus.PendingRetirement, ItemStatus.Retired].includes(statusId)
      ) {
        canEdit = false;
      }
      break;
    case 'Item.practice':
      if (
        isWIP(statusId) ||
        isLightUpdate(statusId) ||
        isPendingRepublishing(statusId) ||
        isPublishedOrCompletedInCPP(statusId)
      ) {
        canEdit = true;
      }
      break;
    case 'Item.primaryQuestion':
      if (contentType == ContentType.MARKET_DEFINITION) {
        canEdit = false;
      }
      break;
    case 'Item.complimentaryResearch':
      if (
        isPublished(statusId) ||
        isLightUpdate(statusId) ||
        isPendingRepublishing(statusId)
      ) {
        canEdit = true;
      } else {
        canEdit = false;
      }
      break;
    case 'Item.pilotType':
      if (isWIP(statusId)) {
        canEdit = true;
      }
      break;
  }

  if (canEdit && schemaField.editableCriteria?.length) {
    return getEditableCriteriaPermission(p);
  }

  return { canView: true, canEdit };
};

const disableRPAndPJsFields = (schemaField: any, data: any): boolean => {
  const type = schemaField?.dataSchema.props?.type;
  if (![NodeType.RolePriority, NodeType.PriorityJourneyStep].includes(type)) {
    return false;
  }

  const isActiveWithId = data?.props?.isActive && data?.props?.id != null;
  const field = schemaField?.field;

  if (field === 'featuredInsights') {
    return !isActiveWithId;
  }

  const allowedFields = ['name', 'description', 'delegate', 'conciseName'];
  return !allowedFields.includes(field) && isActiveWithId;
};

const getEditableCriteriaPermission: GetPermissionFn = ({
  data,
  workspace,
  schemaField,
  hasRole,
  hasJobRole
}): Permission => {
  const cppDataRefIds = getCPPItemDataRefIds(schemaField.dataSchema, data);
  cppDataRefIds.push(workspace.props.refId);
  for (const editableCriteria of schemaField.editableCriteria) {
    const rs = editableCriteria.roles as any[];
    if (rs?.length) {
      if (hasRole(...rs) || hasJobRole(...rs) || hasRole(Role.admin)) {
        const dataRefIds = editableCriteria.dataRefIds;
        if (
          !dataRefIds ||
          getIntersection(cppDataRefIds, dataRefIds).length > 0
        ) {
          return { canView: true, canEdit: true };
        }
      }
    }
  }
  return { canView: true, canEdit: false };
};

const getCPPItemDataRefIds = (schema: Schema, data: Partial<Metadata>) => {
  const refIds = [];
  const cppFields = schema.props.fields.filter(
    f => f.isSystemField && f.fieldType == FieldType.reference
  );
  for (const f of cppFields) {
    const refs = data.props[f.field] || [];
    for (const ref of refs) {
      if (ref?.props?.refId) refIds.push(ref.props.refId);
    }
  }
  return refIds;
};

const getMaintenceCOEFieldPermission: GetPermissionFn = ({
  hasRole,
  schemaField,
  isEditMode
}): Permission => {
  if (isAuditType(schemaField.relationType) && !isEditMode) {
    return { canView: false, canEdit: false };
  }
  if (hasRole(Role.admin, Role.maintenanceCOE)) {
    return { canView: true, canEdit: true };
  }
  return { canView: true, canEdit: false };
};

const getBacktagFieldPermission: GetPermissionFn = ({
  hasRole,
  workspace
}): Permission => {
  const lockStagedMetadata = workspace?.setting?.props?.lockStagedMetadata
    ? true
    : false;
  if (lockStagedMetadata) {
    if (hasRole(Role.admin, Role.backtagAdmin)) {
      return { canView: true, canEdit: true };
    }
  } else {
    if (hasRole(Role.admin, Role.backtagAdmin, Role.backtagger)) {
      return { canView: true, canEdit: true };
    }
  }

  return { canView: true, canEdit: false };
};

const getNonItemFieldPermission = (p: GetPermissionPayload) => {
  const {
    data,
    workspace,
    schemaField,
    isWorkspaceOwner,
    isWorkspaceDataManager,
    hasRole,
    isPracticeAdmin
  } = p;
  if (schemaField.readOnly) {
    return { canView: true, canEdit: false };
  }

  if (schemaField?.dataSchema.labels.includes(NodeType.WorkspaceSchema)) {
    if (hasRole(Role.admin) || isWorkspaceOwner) {
      return { canView: true, canEdit: true };
    } else {
      if (
        schemaField?.dataSchema?.id == AdminDomain.stagedTags &&
        hasRole(Role.backtagAdmin)
      ) {
        return { canView: true, canEdit: true };
      }
      return { canView: true, canEdit: false };
    }
  }

  const dataType = schemaField?.dataSchema?.props?.type;

  const lockedNodeTypes = getLockedNodeTypes(workspace);

  if (lockedNodeTypes.includes(dataType)) {
    if (
      schemaField.key != 'RolePriority.delegate' &&
      schemaField.key != 'PriorityJourneyStep.delegate'
    ) {
      return { canView: true, canEdit: false };
    }
  }

  if (schemaField.editableCriteria?.length) {
    return getEditableCriteriaPermission(p);
  }

  if (disableRPAndPJsFields(schemaField, data)) {
    return { canView: true, canEdit: false };
  }

  // Enterpise Schema fields
  if (
    schemaField?.dataSchema?.props?.isManaged &&
    hasRole(Role.admin, Role.backtagAdmin, Role.questionManager)
  ) {
    return { canView: true, canEdit: true };
  }

  if (schemaField.dataSchema?.isSystemSchema && schemaField.isSystemField) {
    if (
      dataType == NodeType.Practice &&
      ['backupCor', 'cor', 'mgscRep', 'market', 'emtLead']?.includes(
        schemaField?.field
      ) &&
      (hasRole(Role.admin) || isPracticeAdmin())
    ) {
      return { canView: true, canEdit: true };
    }
    return { canView: true, canEdit: false };
  }
  // Audit fields
  if (isAuditType(schemaField.relationType)) {
    return { canView: true, canEdit: false };
  }
  // Parent Schema fields
  if (schemaField.isInherited) {
    return { canView: true, canEdit: false };
  }
  // Custom Schema fields
  return { canView: true, canEdit: isWorkspaceOwner || isWorkspaceDataManager };
};
